import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "text-field-label text-caption" }
const _hoisted_2 = { class: "text-body1" }
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = ["innerHTML"]
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = { class: "flex no-wrap items-center gap-md" }
const _hoisted_7 = ["innerHTML"]

import * as Sentry from "@sentry/browser"
import { SendFeedbackParams } from "@sentry/types/types/feedback/sendFeedback"
import { sanitize } from "dompurify"
import { useDialogPluginComponent } from "quasar"
import SfxBtn from "src/components/button/SfxBtn.vue"
import SfxBtnOutline from "src/components/button/SfxBtnOutline.vue"
import SfxInput from "src/components/input/SfxInput.vue"
import SfxTextarea from "src/components/input/SfxTextarea.vue"
import { LABELS } from "src/env/labels"
import router from "src/router"
import { useAuthStore } from "src/stores/authStore"
import { useTopNavigationStore } from "src/stores/topNavigationStore"
import { useUserStore } from "src/stores/userStore"
import { Log } from "src/util/classes/log"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import { showErrorNotification, showSuccessNotification } from "src/util/functions/notifications"
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits

const MAX_SENTRY_TAG_VALUE = 200

const CHECKBOX_SIZE = "56px"


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxDialogFeedback',
  props: {
  context: {
    type: String,
  },

  designation: {
    type: String,
  },
},
  setup(__props) {

const props = __props

//endregion

//region Variables

const { dialogRef, onDialogCancel, onDialogOK } = useDialogPluginComponent()

const { t } = useI18n()
const userStore = useUserStore()
const authStore = useAuthStore()

// 200 characters is the limit of Sentry tags
// If it exceeds the character limit, the tag is ignored
const titleLabel = t("sfxDialogFeedback.title")
const contextLabel = t("sfxDialogFeedback.context")
const subjectLabel = t("sfxDialogFeedback.subject_label")
const longTextLabel = t("sfxDialogFeedback.long_text_label")
const noticeLabel = t("sfxDialogFeedback.notice_label", { company: LABELS.COMPANY })
const bugLabel = t("sfxDialogFeedback.bug_label")
const sendLabel = t("sfxButtons.btn_send")
const cancelLabel = t("sfxButtons.btn_cancel")

const subjectModel = ref<string>("")
const longTextModel = ref<string>("")
const isBug = ref(false)

const detailedMessage = computed<string>(() => {
  let message = subjectModel.value

  if (longTextModel.value) {
    // Adding double new line for readability
    message += "\r\n\r\n" + longTextModel.value
  }

  return message
})

const piniaTopNavigationPath = computed(() => useTopNavigationStore().title)
const currentRouterPath = computed(() => router.currentRoute.value.name as string)

const contextWithDefaults = computed(
  () => props.context || piniaTopNavigationPath.value || currentRouterPath.value
)
const cleanContext = computed(() => sanitize(contextWithDefaults.value || ""))
const cleanDesignation = computed(() => sanitize(props.designation || ""))

//endregion

//region Functions

function saveData() {
  const feedbackData: SendFeedbackParams = {
    message: detailedMessage.value,
    name: userStore.getFullName,
    email: userStore.email,
  }

  const tags: Record<string, string> = {
    context: cleanContext.value.slice(0, MAX_SENTRY_TAG_VALUE),
    userUUID: userStore.id,
    bug: isBug.value.toString(),
    piniaTopNavigationPath: piniaTopNavigationPath.value,
    currentRouterPath: currentRouterPath.value,
  }

  if (authStore.selectedWorkspace?.id) {
    tags.workspaceUUID = authStore.selectedWorkspace.id
  }

  if (cleanDesignation.value) {
    // While our designations currently have a limit of 100, better be safe and try to future-proof it a little
    tags.designation = cleanDesignation.value.slice(0, MAX_SENTRY_TAG_VALUE)
  }

  // We don't have Sentry enabled, we are probably in development mode.
  // So we just print out the feedback in the console.
  if (Sentry.getClient() === undefined) {
    console.table(feedbackData)
    console.table(tags)
    return
  }

  feedbackData.tags = tags

  const result = Sentry.captureFeedback(feedbackData)

  if (result) {
    showSuccessNotification(t("sfxDialogFeedback.feedback_success"))
    onDialogOK()
  } else {
    showErrorNotification(t("sfxDialogFeedback.feedback_error"))
  }
}

//endregion

//region Setup Code

watch(
  () => props.context,
  (contextVal) => {
    if (!contextVal) {
      Log.warning({
        message: "Forgotten Feedback value",
        data: {
          piniaTopNavigationPath: piniaTopNavigationPath.value,
          currentRouterPath: currentRouterPath.value,
        },
      })
    }
  },
  { immediate: true }
)

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_checkbox = _resolveComponent("q-checkbox")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_dialog = _resolveComponent("q-dialog")!

  return (_openBlock(), _createBlock(_component_q_dialog, {
    ref_key: "dialogRef",
    ref: dialogRef,
    class: "sfx-dialog-feedback",
    persistent: ""
  }, {
    default: _withCtx(() => [
      _createVNode(_component_q_card, { class: "q-dialog-plugin" }, {
        default: _withCtx(() => [
          _createVNode(_component_q_card_section, { class: "text-h3" }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(titleLabel)), 1)
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_section, null, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, _toDisplayString(_unref(contextLabel)), 1),
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("b", {
                  class: "text-info",
                  innerHTML: cleanContext.value
                }, null, 8, _hoisted_3),
                _createTextVNode("  "),
                (props.designation)
                  ? (_openBlock(), _createElementBlock("span", {
                      key: 0,
                      innerHTML: cleanDesignation.value
                    }, null, 8, _hoisted_4))
                  : _createCommentVNode("", true),
                _createElementVNode("div", { innerHTML: _unref(noticeLabel) }, null, 8, _hoisted_5)
              ])
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_section, null, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_6, [
                _createVNode(_component_q_checkbox, {
                  size: CHECKBOX_SIZE,
                  class: "sfx-checkbox",
                  "checked-icon": _unref(Icons).ACCEPT,
                  modelValue: isBug.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((isBug).value = $event))
                }, null, 8, ["checked-icon", "modelValue"]),
                _createElementVNode("span", {
                  innerHTML: _unref(bugLabel),
                  class: "text-body1"
                }, null, 8, _hoisted_7)
              ])
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_section, null, {
            default: _withCtx(() => [
              _createVNode(SfxInput, {
                modelValue: subjectModel.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((subjectModel).value = $event)),
                required: "",
                class: "q-mb-sm hide-bottom-space",
                label: _unref(subjectLabel),
                "hide-bottom-space": ""
              }, null, 8, ["modelValue", "label"]),
              _createVNode(SfxTextarea, {
                modelValue: longTextModel.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((longTextModel).value = $event)),
                placeholder: _unref(longTextLabel)
              }, null, 8, ["modelValue", "placeholder"])
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_section, { class: "flex full-width gap-md" }, {
            default: _withCtx(() => [
              _createVNode(SfxBtnOutline, {
                class: "col",
                label: _unref(cancelLabel),
                onClick: _unref(onDialogCancel)
              }, null, 8, ["label", "onClick"]),
              _createVNode(SfxBtn, {
                class: "col",
                type: "submit",
                label: _unref(sendLabel),
                disable: !subjectModel.value,
                onClick: saveData
              }, null, 8, ["label", "disable"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 512))
}
}

})